<template>
	<div class="layout-fine">
		<Card class="layout-fine__content content">
			<template #content>
				<div class="content__header">
					<Button
						:label="infoRouteBack.title"
						icon="pi pi-arrow-left"
						class="content__header-route text-xl"
						text
						@click="returnToPage" />

					<div class="content__header-title">
						<Tag
							:value="statusName"
							class="text-xs"
							:severity="statusSeverity" />

						<h2 class="text-3xl">Постановление №{{ getLayoutDataAttributes.resolution_number }}</h2>
					</div>
				</div>

				<Divider />

				<div class="content__info">
					<div class="content__info-box">
						<div
							v-for="info in infoItems"
							:key="info.name"
							class="content__info-item">
							<p class="content__info-item-name">{{ info.name }}</p>

							<p class="content__info-item-desc">{{ info.value }}</p>
						</div>

						<ClientOnly>
							<div
								v-if="photosViolationAttached"
								class="content__info-item content__info-item--full">
								<p class="content__info-item-name">Фото фиксации нарушения</p>

								<Galleria
									:value="getPhotos"
									container-class="detail-galleria"
									:thumbnails-position="thumbnailsPosition"
									:vertical-thumbnail-view-port-height="'auto'">
									<template #item="{ item }">
										<img
											:src="item"
											alt="Фото нарушения" />
									</template>

									<template #thumbnail="{ item }">
										<img
											:src="item"
											alt="Фото нарушения" />
									</template>
								</Galleria>
							</div>
						</ClientOnly>
					</div>
				</div>

				<Divider />

				<div
					v-if="!isPaid"
					class="content__footer">
					<ApplicationContestingInfo
						v-if="getLayoutDataAttributes.complaint"
						:guid="getLayoutDataAttributes.complaint"
						:type="getLayoutType" />

					<Challenge
						v-else
						:challenge-link="'application-contesting'"
						:page-type="getLayoutType" />
				</div>
			</template>
		</Card>

		<Payment
			:id="`${route.params.id}`"
			:data="getLayoutDataAttributes"
			:type="getLayoutType" />
	</div>
</template>

<script setup lang="ts">
	import { PaymentStatus } from "@/assets/ts/models/payment/PaymentStatus";
	import { LayoutDetailWithPayment } from "@/assets/ts/models/detail-with-payment/LayoutDetailWithPayment";
	import { type ILayoutDetailWithPayment } from "@/assets/ts/interfaces/detail-with-payment/ILayoutDetailWithPayment";
	import { LayoutTypes } from "@/assets/ts/enums/detail-with-payment/LayoutTypes";
	import { PageTitles } from "@/assets/ts/enums/PageTitles";
	import { useCheckingValue } from "@/composables/useCheckingValue";
	import type { IRouterBack } from "@/assets/ts/d/page-meta/interfaces/IRouterBack";

	const route = useRoute();
	const router = useRouter();

	const props = defineProps<{ data: ILayoutDetailWithPayment }>();

	const { getLayoutDataAttributes, photosViolationAttached, getPhotos, getLayoutType } = new LayoutDetailWithPayment(props.data);

	const { statusName, statusSeverity } = new PaymentStatus(getLayoutDataAttributes.payment_status);

	const isPaid = getLayoutDataAttributes.payment_status === "paid";

	const viewport = useViewport();

	const thumbnailsPosition = computed(() => (viewport.isLessThan("xxl") ? "bottom" : "right"));

	const infoRouteBack = computed(() => route.meta.route_back?.find((info) => route.matched.some((item) => item.path === info.matchingPath)) as IRouterBack);

	const returnToPage = () => {
		router.push(infoRouteBack.value.route);
	};

	const layoutTitlesMap: Record<LayoutTypes, PageTitles> = {
		[LayoutTypes.Penalty]: PageTitles.Penalty,
		[LayoutTypes.TollRoads]: PageTitles.TollRoads,
	};

	const layoutDateMap: Record<LayoutTypes, string> = {
		[LayoutTypes.Penalty]: "штрафа",
		[LayoutTypes.TollRoads]: "проезда",
	};

	const title = computed(() => {
		const title = layoutTitlesMap[getLayoutType as LayoutTypes];

		return title || "Неизвестный тип";
	});

	const dateTitle = computed(() => {
		const text = layoutDateMap[getLayoutType as LayoutTypes];

		return text ? `Дата ${text}` : "Дата";
	});

	useHead({ title });

	const infoItems = computed(() => [
		{
			name: "Автомобиль",
			value: `${useCheckingValue(getLayoutDataAttributes.auto.model)}, ${useCheckingValue(getLayoutDataAttributes.auto.number)}`,
		},
		{
			name: dateTitle.value,
			value: useFormatDate(useCheckingValue(getLayoutDataAttributes.date)),
		},
		{
			name: "Источник постановления",
			value: useCheckingValue(getLayoutDataAttributes.source),
		},
		{
			name: "Срок оплаты",
			value: useFormatDate(useCheckingValue(getLayoutDataAttributes.payment_term)),
		},
	]);
</script>

<style scoped lang="scss">
	@import "@/assets/scss/layouts/fine/styles";
</style>
